// Wraps any block (chart, table, etc) with a small hover-revealed
// "↓ png" affordance that exports the wrapped subtree using the
// shared dashboard rail/padding chrome.
//
// The export logic is also exported as a standalone async helper
// (`triggerExportPng`) so non-React callers — e.g. turn-level export
// buttons living in larger components — can use the same defaults.

import { useCallback, useRef, useState } from "react";
import { ORIGIN_TALL_SVG } from "../lib/shell.js";
import { sanitizeLocalDownloadHref } from "../lib/renderSafety.js";

export async function triggerExportPng(el, filename) {
    if (!el) return;
    // Lazy-load the rasterizer so the SVG/foreignObject machinery
    // only enters the bundle on first export.
    const { exportNodeAsPng } = await import("../lib/exportNodePng.js");
    const rootStyles = getComputedStyle(document.documentElement);
    const bg = rootStyles.getPropertyValue("--bg").trim() || "#000";
    const railBg = rootStyles.getPropertyValue("--rail-bg").trim() || "#080807";
    const railInk = rootStyles.getPropertyValue("--rail-ink").trim() || "#f3efe8";
    const dataUrl = await exportNodeAsPng(el, {
        pixelRatio: 4,
        backgroundColor: bg,
        padding: 14,
        leftRail: {
            html: ORIGIN_TALL_SVG,
            width: 14,
            innerPadding: 14,
            background: railBg,
            color: railInk,
        },
    });
    const href = sanitizeLocalDownloadHref(dataUrl);
    if (!href) return;
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

export default function ExportableBlock({ filenamePrefix, children, className = "" }) {
    const ref = useRef(null);
    const [exporting, setExporting] = useState(false);
    const onExport = useCallback(async (e) => {
        e.stopPropagation();
        if (exporting) return;
        setExporting(true);
        try {
            await triggerExportPng(ref.current, `${filenamePrefix}-${Date.now()}.png`);
        } catch (err) {
            console.warn("[chat] block export failed:", err);
        } finally {
            setExporting(false);
        }
    }, [exporting, filenamePrefix]);
    return (
        <div ref={ref} className={`group relative ${className}`}>
            <button
                type="button"
                onClick={onExport}
                disabled={exporting}
                data-export-ignore="true"
                title="Export as PNG"
                aria-label="Export as PNG"
                className="absolute top-[4px] right-[4px] z-20 cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[6px] py-[1px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] opacity-0 transition-opacity hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] group-hover:opacity-100 disabled:opacity-50 disabled:cursor-wait"
            >
                {exporting ? "…" : "↓ png"}
            </button>
            {children}
        </div>
    );
}
